import { cn } from '@/lib/utils';
import { FormError } from '@chiroup/core/types/ErrorResponse.type';
import React, { ChangeEvent, useState } from 'react';
import {
  NumericFormat,
  PatternFormat,
  type NumberFormatValues,
  type SourceInfo,
} from 'react-number-format';
import { TrivialTooltip } from '../TrivialTooltip';
import { ErrorIcon } from './ErrorIcon';
import { FieldErrors } from './FieldErrors';

type Props = {
  name: string;
  label?: string | React.ReactNode;
  errors?: FormError;
  onChange: (val: any) => void;
  value?: string | number | null;
  className?: string;
  placeholder?: string;
  onBlur?: (val: string) => void;
  hint?: string | React.ReactNode;
  hintOnClick?: () => void;
  disabled?: boolean;
  tooltip?: string;
  inputTitle?: string;
  patternFormat?: string;
  numericOptions?: any;
  inline?: boolean;
  inlineLabelClassName?: string;
  inlineInputClassName?: string;
  autoFocus?: boolean;
  textAlign?: 'left' | 'center' | 'right';
};

export const InputMasked = ({
  label,
  name,
  onChange,
  value,
  errors,
  className = '',
  placeholder,
  onBlur,
  hint,
  hintOnClick,
  disabled = false,
  tooltip,
  inputTitle,
  patternFormat,
  numericOptions = {},
  inline = false,
  inlineLabelClassName,
  inlineInputClassName,
  autoFocus = false,
  textAlign = 'left',
}: Props) => {
  const [numericFocused, setNumericFocused] = useState(false);

  const onChangeCleave = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    onChange(val);
  };

  const onNumericValueChange = (values: NumberFormatValues, sourceInfo: SourceInfo) => {
    if (sourceInfo.source === 'event') {
      onChange(values.value);
    }
  };

  const onBlurCleave = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    onBlur?.(val);
  };

  // Get text alignment class
  const textAlignClass =
    textAlign === 'right' ? 'text-right' : textAlign === 'center' ? 'text-center' : 'text-left';

  // Normalize value to prevent react-number-format library bug with undefined/null values
  const normalizedValue = value ?? '';

  return (
    <div className={cn(className, 'flex flex-col gap-1')}>
      {label && (
        <div className={inline ? 'align-top pr-4 pt-0 inline-block border border-green-500' : ''}>
          <label
            htmlFor={name}
            className={cn(
              inline ? 'inline-block' : 'block',
              inlineLabelClassName ? inlineLabelClassName : '',
              'text-sm font-medium leading-5 text-gray-900 dark:text-dark-gray-200'
            )}
          >
            {label}
            {tooltip && <TrivialTooltip text={tooltip} type="info" />}
          </label>
        </div>
      )}
      <div className={cn(inline ? 'inline-block' : '', inline && inlineInputClassName)}>
        <div className="relative rounded-md">
          {patternFormat ? (
            <PatternFormat
              format={patternFormat}
              data-slot="input"
              className={cn(
                // Base shadcn UI input styles
                'file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input flex h-9 w-full min-w-0 rounded-md border px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm bg-white dark:bg-dark-gray-700',
                'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:border-primary-500 focus-visible:ring-primary-500',

                // Text alignment
                textAlignClass,

                // Existing functionality styles
                errors
                  ? 'aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive border-destructive text-destructive placeholder:text-destructive/50'
                  : '',
                disabled
                  ? 'text-gray-500 dark:text-dark-gray-300 bg-gray-300 dark:bg-dark-gray-500 cursor-not-allowed'
                  : ''
              )}
              onChange={onChangeCleave}
              onBlur={onBlurCleave}
              value={normalizedValue as string}
              placeholder={placeholder}
              disabled={disabled}
              title={inputTitle}
              autoFocus={autoFocus}
              name={name}
            />
          ) : (
            <NumericFormat
              data-slot="input"
              className={cn(
                // Base shadcn UI input styles
                'file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm bg-white dark:bg-dark-gray-700',
                'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:border-primary-500 focus-visible:ring-primary-500',

                // Text alignment
                textAlignClass,

                // Existing functionality styles
                errors
                  ? 'aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive border-destructive text-destructive placeholder:text-destructive/50'
                  : '',
                disabled
                  ? 'text-gray-500 dark:text-dark-gray-300 bg-gray-300 dark:bg-dark-gray-500 cursor-not-allowed'
                  : ''
              )}
              onValueChange={onNumericValueChange}
              onFocus={() => setNumericFocused(true)}
              onBlur={e => {
                setNumericFocused(false);
                onBlurCleave(e);
              }}
              value={normalizedValue as string}
              placeholder={placeholder}
              disabled={disabled}
              title={inputTitle}
              autoFocus={autoFocus}
              {...numericOptions}
              fixedDecimalScale={
                numericFocused ? false : (numericOptions?.fixedDecimalScale ?? false)
              }
              name={name}
            />
          )}
          {errors && <ErrorIcon />}
        </div>
        {inline ? null : <FieldErrors errors={errors} />}
        {!!hint && (
          <p
            className={[
              'mt-2 text-sm',
              hintOnClick
                ? 'text-primary-600 font-semibold hover:text-primary-500 cursor-pointer italic'
                : 'text-gray-500 italic',
            ].join(' ')}
            id={`${name}-hint`}
            onClick={hintOnClick}
          >
            {hint}
          </p>
        )}
        {inline ? <FieldErrors errors={errors} /> : null}
      </div>
      {/* {inline ? 'inline' : 'block'} */}
    </div>
  );
};
